import {
  breakpointSizes,
  ButtonDesigns,
  ButtonTypes,
  getBreakpointDown,
  styleConstants,
} from '@paywithextend/components'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectTransactionAttachmentIdFromState,
  selectTransactionIdFromPathState,
  selectUploadingAttachmentsFromState,
} from 'src/app/TransactionDetail/redux/transactionDetail.selectors'
import CardDetailsSection from 'src/app/TransactionDetail/Sections/CardDetailsSection'
import ExpenseDetailsSection from 'src/app/TransactionDetail/Sections/ExpenseDetailsSection'
import ReceiptsSection from 'src/app/TransactionDetail/Sections/ReceiptsSection'
import TransactionSummarySection from 'src/app/TransactionDetail/Sections/TransactionSummarySection'
import Button from 'src/components/Button/Button'
import CloseButton, {
  CloseButtonDesigns,
} from 'src/components/CloseButton/CloseButton'
import LoadingRightFlyoutContent from 'src/components/LoadingRightFlyoutContent/LoadingRightFlyoutContent'
import NotesInput from 'src/components/NotesInput/NotesInput'
import NotesInputBox from 'src/components/NotesInputBox/NotesInputBox'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import ReceiptsBox from 'src/components/ReceiptsBox/ReceiptsBox'
import RemoveTransactionPopup from 'src/components/RemoveTransactionPopup/RemoveTransactionPopup'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import TestId from 'src/components/TestId/TestId'
import TransactionCardDetailsBox from 'src/components/TransactionCardDetailsBox/TransactionCardDetailsBox'
import TransactionExpenseDetailsBox from 'src/components/TransactionExpenseDetailsBox/TransactionExpenseDetailsBox'
import TransactionSummaryBox from 'src/components/TransactionSummaryBox/TransactionSummaryBox'
import { useDebounced, useFlyoutDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { TransactionSource } from 'src/models/transaction.models'
import { closeModal } from 'src/navigation/Lnk'
import { useQueryCreditCardPermissions } from 'src/queries/creditCard.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import {
  usePatchTransaction,
  usePutTransactionNotes,
  useQueryTransactionDetail,
  useQueryTransactionsPerson,
} from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { useQueryVirtualCardDetail } from 'src/queries/virtualCards.queries'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'
import {
  UPLOAD_ATTACHMENT_SUCCESS,
  uploadAttachments,
  UploadAttachmentSuccessAction,
} from 'src/redux/actions/data/receiptAttachments.actions'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { PatchTransactionRequest } from 'src/requests/patchTransaction.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

import style from './TransactionDetail.style'

const boxCss = css`
  width: 100%;
  margin-top: ${styleConstants.heights.minorInternalGutter};

  ${getBreakpointDown(breakpointSizes.XSM)(css`
    margin-top: 10px;
  `)}
`

const TransactionDetail: React.FC = () => {
  const dispatch = useFlyoutDispatch()
  const transactionId = useSelector(selectTransactionIdFromPathState)
  const attachmentId = useSelector(selectTransactionAttachmentIdFromState)
  const {
    data: transaction,
    error: fetchFailure,
    isSuccess: fetchSuccess,
  } = useQueryTransactionDetail({ transactionId })
  const { data: virtualCard } = useQueryVirtualCardDetail({
    virtualCardId: transaction?.virtualCardId,
  })
  const { data: user } = useQueryOwnUser({})
  const { data: cardUser } = useQueryTransactionsPerson(
    transaction?.recipientId,
  )
  const { data: cardCreator } = useQueryTransactionsPerson(
    virtualCard?.cardholderId,
  )
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: transaction?.creditCardId,
    retry: false,
  })
  const { data: parentCreditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: transaction?.parentCreditCardId,
    retry: false,
  })
  // const { data: creditCard } = useQueryCreditCard({ creditCardId: transaction?.creditCardId, enabled: creditCardPermissions !== undefined && creditCardPermissions.permissions.length > 0 })

  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const { analytics } = useAnalyticsContext()
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const {
    mutate: putTransactionNotes,
    isPending: puttingTransactionsNotes,
    isSuccess: puttingTransactionsNotesComplete,
    error: puttingTransactionsNotesError,
  } = usePutTransactionNotes()
  const { mutate: patchTransaction, error: attachmentsTopLevelError } =
    usePatchTransaction()

  const [isNotesChanged, setIsNotesChanged] = useState<boolean>(false)
  const [notes, setNotes] = useState<string | undefined>(transaction?.notes)
  const [openInitialAttachment, setOpenInitialAttachment] =
    useState<boolean>(true)
  const [
    isRemoveTransactionConfirmationPopupOpen,
    setIsRemoveTransactionConfirmationPopupOpen,
  ] = useState(false)

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    dispatch(uploadAttachments(attachments)).then((response) => {
      const successfulResponses = response.filter(
        (resp) => resp.type === UPLOAD_ATTACHMENT_SUCCESS,
      ) as UploadAttachmentSuccessAction[]
      patchTransaction({
        transactionId,
        body: {
          receiptAttachmentIds: [
            ...successfulResponses.map((successful) => successful.payload.id),
            ...(transaction?.receiptAttachments.map((ra) => ra.id) || []),
          ],
        },
      })
    })
    analytics?.track('transaction detail - upload attachment - button press', {
      merchant: transaction?.merchantName,
      merchantCategory:
        transaction?.merchantName !== undefined
          ? transaction?.mccGroup
          : undefined,
      transactionDate: transaction
        ?.clearingOrAuthDateMoment(false)
        ?.format('l'),
      clearedDate: transaction?.clearingOrAuthDateMoment(true)?.format('L'),
      card: transaction?.vcnDisplayName,
      totalNumberOfAttachments: attachments.length,
      transactionId: transactionId,
      status: transaction?.status,
    })
  }

  const dispatchPatchTransaction = (
    body: Partial<PatchTransactionRequest>,
  ): void => {
    patchTransaction({ transactionId, body })
  }

  const deleteAttachment = (attachmentId: string): void => {
    const newAttachments = transaction?.receiptAttachments.filter(
      (tr) => tr.id !== attachmentId,
    )
    patchTransaction({
      transactionId,
      body: {
        receiptAttachmentIds: newAttachments?.map((tr) => tr.id),
      },
    })
  }

  const { debouncedF } = useDebounced((): void => {
    if (notes !== undefined && transaction?.notes !== notes) {
      putTransactionNotes({ transactionId, body: { notes } })
      setIsNotesChanged(true)
    }
  }, 1000)

  useEffect(() => {
    if (isNotesChanged) {
      analytics?.track(
        'transaction detail - manipulate transaction notes - component exit',
        {
          screen: `/transactions/${transactionId}`,
          interaction: 'component exit',
          intent: 'Click into & add string values to notes',
          notes: notes,
        },
      )
    }
  }, [isNotesChanged])

  useEffect(() => {
    if (notes === undefined && transaction?.notes !== undefined) {
      setNotes(transaction.notes)
    }
  }, [transaction])

  useEffect(() => {
    debouncedF()
  }, [notes])

  useEffect((): void => {
    const attachment = transaction?.receiptAttachments.find((attachment) => {
      return attachment.id === attachmentId
    })

    if (openInitialAttachment && attachment) {
      dispatch(
        createSetIsAttachmentPopupOpen(
          true,
          (): void => {
            deleteAttachment(attachment.id)
          },
          attachment,
        ),
      )
      setOpenInitialAttachment(false)
    }
  }, [attachmentId, transaction?.receiptAttachments])

  const { data: expCatSchema } = useQueryExpenseCategories({
    filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
  })

  const showTransactionFlyoutRevamp = useSelector(
    selectFeatureFlaggerFromState,
  )?.show_transaction_flyout_revamp
  if (showTransactionFlyoutRevamp) {
    // Keeping the flyout as a page for now to restrict scope
    // When this is finally updated to not be a page, we will
    // need to update all sorts of urls
    // But also moving to BaseFlyout to make the eventual shift
    // as simple as possible

    return (
      <BaseFlyout
        className={'TransactionFlyout'}
        isOpen={true}
        onClose={() => dispatch(closeModal())}
        css={style}
      >
        {transaction === undefined || cardUser === undefined ? (
          <LoadingRightFlyoutContent />
        ) : (
          <>
            {/* NOTE: Sections are surrounded by divs so that their styling
             * does not interfer with the separators
             */}
            <div>
              <TransactionSummarySection
                transaction={transaction}
                cardUser={cardUser}
              />
            </div>
            {/* <div>{'Manager Reviews Section - coming soon!'}</div> */}
            <div>
              <ReceiptsSection
                transaction={transaction}
                attachments={transaction.receiptAttachments}
                uploadAttachments={dispatchUploadAttachments}
                deleteAttachment={deleteAttachment}
                uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
                topLevelError={attachmentsTopLevelError?.error}
                isReceiptRequired={
                  !!receiptManagementEnabled && transaction.receiptRequired
                }
                isReceiptSectionVisible={
                  user?.hasOrgBookkeeperPermissions &&
                  transaction.organizationReceiptRequired
                }
                receiptRule={organization?.receiptRule}
                patchTransaction={dispatchPatchTransaction}
                isTransaction
              />
            </div>
            {expCatSchema && expCatSchema.length > 0 && (
              <div>
                <ExpenseDetailsSection transaction={transaction} />
              </div>
            )}
            <div>
              <NotesInput
                notesProps={{
                  showUpdates: true,
                  baseValue: transaction.notes,
                  value: notes,
                  onChange: (val: string): void => {
                    setNotes(val)
                  },
                  puttingNotes: puttingTransactionsNotes,
                  puttingNotesComplete: puttingTransactionsNotesComplete,
                  puttingNotesError: puttingTransactionsNotesError?.error,
                }}
                maxLength={2000}
                notesCharacters={notes?.length ?? 0}
              />
            </div>
            <div>
              <CardDetailsSection
                transaction={transaction}
                creditCardPermissions={creditCardPermissions}
                parentCreditCardPermissions={parentCreditCardPermissions}
                cardUser={cardUser}
                cardCreator={cardCreator}
              />
            </div>
          </>
        )}
      </BaseFlyout>
    )
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Transaction}>
      {transaction !== undefined ? (
        <div
          css={css`
            padding: ${styleConstants.heights.rightFlyoutPaddingSMUp}
              ${styleConstants.widths.rightFlyoutPaddingSMUp};

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              padding: ${styleConstants.heights.rightFlyoutPaddingXSMDown}
                ${styleConstants.widths.rightFlyoutPaddingXSMDown};
            `)}
          `}
        >
          {isRemoveTransactionConfirmationPopupOpen && (
            <RemoveTransactionPopup
              transaction={transaction}
              closeFunction={setIsRemoveTransactionConfirmationPopupOpen}
            />
          )}
          <CloseButton
            css={css`
              flex-shrink: 0;

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                align-self: flex-end;
              `)}
            `}
            design={CloseButtonDesigns.DarkOnLight}
            onClick={(): void => {
              dispatch(closeModal())
            }}
            id="closeTransactionDetailFlyout"
          />
          <TransactionSummaryBox css={boxCss} transaction={transaction} />
          <TransactionExpenseDetailsBox
            css={boxCss}
            testId="expense-categories-box"
          />
          <ReceiptsBox
            css={boxCss}
            attachments={transaction.receiptAttachments}
            uploadAttachments={dispatchUploadAttachments}
            deleteAttachment={deleteAttachment}
            uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
            topLevelError={attachmentsTopLevelError?.error}
            isReceiptRequired={
              !!receiptManagementEnabled && transaction.receiptRequired
            }
            isReceiptSectionVisible={
              user?.hasOrgBookkeeperPermissions &&
              transaction.organizationReceiptRequired
            }
            receiptRule={organization?.receiptRule}
            patchTransaction={dispatchPatchTransaction}
            isTransaction
          />
          <NotesInputBox
            css={boxCss}
            notesProps={{
              showUpdates: true,
              baseValue: transaction.notes,
              value: notes,
              onChange: (val: string): void => {
                setNotes(val)
              },
              puttingNotes: puttingTransactionsNotes,
              puttingNotesComplete: puttingTransactionsNotesComplete,
              puttingNotesError: puttingTransactionsNotesError?.error,
            }}
          />
          <TransactionCardDetailsBox
            css={boxCss}
            transaction={transaction}
            virtualCard={virtualCard}
            creditCardPermissions={creditCardPermissions}
            parentCreditCardPermissions={parentCreditCardPermissions}
            // creditCard={creditCard}
            cardUser={cardUser}
            cardCreator={cardCreator}
          />
          {user?.hasOrgBookkeeperPermissions &&
            [TransactionSource.Physical, TransactionSource.Other].includes(
              transaction.source,
            ) && (
              <TestId testId={'removeTxnBtn'}>
                <Button
                  design={ButtonDesigns.designRed}
                  type={ButtonTypes.button}
                  text={<Trns source={'Remove Transaction'} />}
                  onClick={(): void =>
                    setIsRemoveTransactionConfirmationPopupOpen(true)
                  }
                  css={css`
                    margin-bottom: 30px;
                  `}
                />
              </TestId>
            )}
        </div>
      ) : fetchFailure || fetchSuccess ? (
        <NotFoundRightFlyoutContent />
      ) : (
        <LoadingRightFlyoutContent />
      )}
    </RightFlyout>
  )
}

export default TransactionDetail
